<template>
    <div class="user-profile">
        <template v-if="loading">
            <div class="h1 skeleton">
                <div class="skeleton-header-wrapper">
                    <div class="skeleton-header">
                        <skeleton height="100%" />
                    </div>
                </div>
            </div>
        </template>
        <template v-else>
            <h1>{{ fullName }}</h1>
        </template>
        <div class="row gx-md-4">
            <div class="col-md-5 col-lg-4 mx-auto u-form">
                <template v-if="loading">
                    <div class="skeleton skeleton-wrapper" v-for="i in [...Array(5).keys()]" :key="i">
                        <skeleton height="100%" />
                    </div>
                </template>
                <template v-else>
                    <form @submit.prevent="saveChanges(!v$.$invalid && !emailInvalid())">
                        <u-floating-label v-if="!isSSO">
                            <InputText id="pers-set-login"
                                       class="p-inputtext-lg"
                                       v-model="login"
                                       disabled
                                       readonly />
                            <label for="pers-set-login">{{ t("userProfile.fields.loginName") }}</label>
                        </u-floating-label>

                        <u-floating-label>
                            <InputText id="pers-set-firstname"
                                       class="p-inputtext-lg"
                                       v-model="account.firstName"
                                       disabled
                                       readonly />
                            <label for="pers-set-firstname">{{ t("userProfile.fields.firstName") }}</label>
                        </u-floating-label>

                        <u-floating-label>
                            <InputText id="pers-set-lastname"
                                       class="p-inputtext-lg"
                                       v-model="account.lastName"
                                       disabled
                                       readonly />
                            <label for="pers-set-lastname">{{ t("userProfile.fields.lastName") }}</label>
                        </u-floating-label>

                        <u-floating-label>
                            <InputText id="pers-set-birthdate"
                                       class="p-inputtext-lg"
                                       v-model="birthDateFormated"
                                       disabled
                                       readonly />
                            <label for="pers-set-birthdate">{{ t("userProfile.fields.birthDate") }}</label>
                        </u-floating-label>

                        <u-floating-label>
                            <InputText id="pers-set-email"
                                       class="p-inputtext-lg"
                                       :modelValue="account.email"
                                       @update:modelValue="updateEmail"
                                       :disabled="saving || isSSO || IsUserFromInternalDistribution"
                                       :class="{'p-invalid': ((v$.email.$invalid || emailInvalid()) && v$.email.$dirty)}" />
                            <label for="pers-set-email">{{ t("userProfile.fields.email") }}</label>
                        </u-floating-label>
                        <u-error-container v-if="(v$.email.$invalid || emailInvalid() || emailChanged()) &&  v$.email.$dirty">
                            <u-error-item v-if="v$.email.required.$invalid">
                                {{ t("userProfile.validations.enterEmail") }}
                            </u-error-item>
                            <u-error-item v-if="emailInvalid()">
                                {{ t("userProfile.validations.invalidEmail") }}
                            </u-error-item>
                            <u-error-item v-if="emailChanged()">
                                {{ t("userProfile.validations.emailChanged") }}
                            </u-error-item>
                        </u-error-container>

                        <u-floating-label v-if="hasHCLRight">
                            <InputText id="pers-set-secondary-email"
                                       class="p-inputtext-lg"
                                       :modelValue="account.secondaryEmail"
                                       @update:modelValue="updateSecondaryEmail"/>
                            <label for="pers-set-secondary-email">{{ t("userProfile.fields.secondaryEmail") }}</label>
                        </u-floating-label>

                        <u-floating-label>
                            <InputText id="pers-set-phone"
                                       class="p-inputtext-lg"
                                       :modelValue="account.phoneNumber"
                                       @update:modelValue="updatePhoneNumber"
                                       :disabled="saving || !account.phoneNumberIsEditable || isSSO"
                                       :class="{'p-invalid': v$.phoneNumber.$invalid && v$.phoneNumber.$dirty}" />
                            <label for="pers-set-phone">{{ t("userProfile.fields.phone") }}</label>
                        </u-floating-label>
                        <u-error-container v-if="v$.phoneNumber.$invalid && v$.phoneNumber.$dirty">

                            <u-error-item v-if="v$.phoneNumber.phoneNumberValid.$invalid">
                                {{ t("userProfile.validations.invalidPhoneNumber") }}
                            </u-error-item>
                        </u-error-container>

                        <u-floating-label>
                            <InputText id="pers-set-broker-name"
                                       class="p-inputtext-lg"
                                       v-model="broker.name"
                                       disabled
                                       readonly />
                            <label for="pers-set-broker-name">{{ t("userProfile.fields.brokerName") }}</label>
                        </u-floating-label>

                        <u-floating-label>
                            <InputText id="pers-set-broker-crn"
                                       class="p-inputtext-lg"
                                       :modelValue="String(broker.crn)"
                                       disabled
                                       readonly />
                            <label for="pers-set-broker-crn">{{ t("userProfile.fields.brokerCrn") }}</label>
                        </u-floating-label>

                        <Button :label="t('userProfile.buttons.submit')"
                                class="p-button-success"
                                type="submit"
                                :disabled="saving || (!v$.email.$dirty && !v$.phoneNumber.$dirty && !v$.secondaryEmail.$dirty)"
                                :icon="savingIcon"
                                iconPos="right"
                                v-if="!isSSO" />
                    </form>

                    <Button :label="t('userProfile.buttons.changePassword')"
                            class="p-button-success"
                            @click="$router.push('change-password')"
                            :disabled="saving"
                            v-if="!isSSO" />
                </template>
            </div>
        </div>
    </div>

    <verification-prompt v-model:visible="showVerifyPrompt" @submited="continueSaving($event)" @resendVerificationCode="resendVerificationCode()" ref="verifyPrompt" />
    <password-verification-prompt v-model:visible="showPasswordVerifyPrompt" @submited="continueSaving($event)" />
    <Dialog position="center" :closable="true" :showHeader="true" :header="t('tiles.messages.warning')"
            v-model:visible="displayModal" id="idDialog">
        {{ modalMessage }}
        <template #footer>
            <Button label="OK" v-on:click="displayModal = false;" />
        </template>
    </Dialog>
    <Dialog position="center" :closable="true" :showHeader="true" :header="t('tiles.messages.warning')"
            v-model:visible="displaySecondaryModal" id="idSecondaryDialog">
        {{ secondaryModalMessage }}
        <template #footer>
            <Button label="OK" v-on:click="displaySecondaryModal = false;" />
        </template>
    </Dialog>
</template>

<script lang="ts">
    import { computed, defineComponent, onMounted, reactive, ref } from 'vue';
    import BrokerClient from '../services/BrokerClient';
    import AccountClient from '../services/AccountClient';
    import AccountDto from '../dtos/AccountDto';
    import UFloatingLabel from '../components/common/UFloatingLabel.vue'
    import BrokerDto from '../dtos/BrokerDto';
    import { useToast } from 'primevue/usetoast';
    import useVuelidate from '@vuelidate/core';
    import { required } from '@vuelidate/validators';
    import UErrorContainer from "@/components/common/UErrorContainer.vue";
    import UErrorItem from "@/components/common/UErrorItem.vue";
    import VerificationPrompt from '../components/VerificationPrompt.vue';
    import PasswordVerificationPrompt from '../components/PasswordVerificationPrompt.vue';
    import TilesClient from '../services/TilesClient';
    import SecondFactorClient from '../services/SecondFactorClient';
    import { useStore } from '../store';
    import { useI18n } from 'vue-i18n';
    import * as EmailValidator from 'email-validator';
    import { useHead } from '@vueuse/head';
    import VerifyEmailUniquenessDto from '../dtos/VerifyEmailUniquenessDto';
    import SecondFactorDto from '../dtos/SecondFactorDto';
    import parseMobile from 'libphonenumber-js/mobile';
    import { useReCaptcha } from 'vue-recaptcha-v3';
    import General from '../help/GeneralFunction';
import TileCmsDto from '../dtos/TileCmsDto';
import { MutationTypes } from '../store/MutationTypes';

    type PatchObject = {
        op: string;
        path: string;
        value: string;
    };

    const diff = require('json-patch-gen') as (obj1: unknown, obj2: unknown) => PatchObject[];

    export default defineComponent({
        components: { UFloatingLabel, UErrorContainer, UErrorItem, VerificationPrompt, PasswordVerificationPrompt },
        setup() {
            const toast = useToast();
            const store = useStore();
            const { t } = useI18n();
            const general = new General();
            const userId = ref(store.state.user?.id ?? "");

            const loading = ref(false), saving = ref(false);
            const savingIcon = computed(() => saving.value ? "pi pi-spin pi-spinner" : "");
            const displayModal = ref(false);
            const displaySecondaryModal = ref(false);
            const accountClient = new AccountClient();
            const broker = reactive(new BrokerDto());
            const modalMessage = ref("");
            const secondaryModalMessage = ref(t('tiles.messages.secondaryEmailEditation'));
            const isSSO = store.state.user?.type == "SSO";
            //SAVEBUTTON - disable save button v pripade ze nebola vykonana ziadna zmena v profile
            //const origAccount = reactive(new AccountDto());
            let origAccount = new AccountDto();
            const account = reactive(new AccountDto());
            const tiles = reactive(Array<TileCmsDto>());

            function mobileValidate(phone: string): boolean {
                const mobileNumber = parseMobile(phone);

                if (mobileNumber) {
                    return mobileNumber.getType() === 'MOBILE';
                }
                return false;
            }

            const secondaryEmailValid = (): boolean =>{
                return account.secondaryEmail == '' || account.secondaryEmail == null || account.secondaryEmail == undefined ? true : EmailValidator.validate(account.secondaryEmail);
            }

            const phoneNumberValid = () => account.phoneNumberIsEditable ?
                (new RegExp('^(\\+|(00))((420)|(421)|(36)|(48))[0-9]{9}$|^(\\+|(00))(((43)|(49))[0-9]{9,11})$')).test(account.phoneNumber) && mobileValidate(account.phoneNumber) :
                true;
            const rules = {
                email: { required },
                phoneNumber: { phoneNumberValid },
                secondaryEmail: { secondaryEmailValid }
            };
            let secFactor: SecondFactorDto;
            const v$ = useVuelidate(rules, account);
            const login = ref('');
            let verificationCodeSent = false;
            const verifyPrompt = ref(null);

            const reCaptcha = useReCaptcha();
            let recaptchaToken = "";

            const getRecaptchaToken = async () => {
                if (!store.getters.isRecaptchaEnabled) {
                    return true;
                }
                recaptchaToken = "";
                // (optional) Wait until recaptcha has been loaded.
                if (reCaptcha) {
                    await reCaptcha.recaptchaLoaded();
                    recaptchaToken = await reCaptcha.executeRecaptcha('resetPassword');
                }

                if (recaptchaToken == null || recaptchaToken == undefined || recaptchaToken == "") {
                    toast.add({
                        severity: 'error',
                        summary: t("toast.titles.error"),
                        detail: t("toast.messages.recaptchaError")
                    });
                    return false;
                }

                return true;
            }

            const emailInvalid = (): boolean => {
                return account.email == '' ? false : !EmailValidator.validate(account.email);
            }

            const emailValidStyle = (): string => {
                return EmailValidator.validate(account.email) ? '' : 'p-invalid';
            }

            const emailChanged = (): boolean => {
                return (account.username == null || account.username == undefined || account.username.length < 1) &&
                    !emailInvalid() && account.email.toLowerCase() != general.cutEmail(origAccount.email).toLowerCase();
            }

            const updateEmail = (value: string | null | undefined) => {
                account.email = value ?? "";
                v$.value.email.$touch();
            };

            const updateSecondaryEmail = (value: string | null | undefined) => {
                account.secondaryEmail = value ?? "";
                v$.value.secondaryEmail.$touch();
            };

            const updatePhoneNumber = (value: string | null | undefined) => {
                account.phoneNumber = value ?? "";
                v$.value.phoneNumber.$touch();
            };

            async function loadMe(): Promise<void> {
                Object.assign(account, await accountClient.getMe());
                login.value = account.username ? account.username : account.email;
                origAccount = new AccountDto(account);
                account.email = general.cutEmail(account.email);
                store.commit(MutationTypes.setUser, new AccountDto(account));
            }

            const isSecondaryEmailEditationOk = (): boolean =>{
                if (tiles.some(x => x.applicationName == 'HCL'))
                {
                    if (account.email == account.secondaryEmail || account.secondaryEmail?.endsWith('uniqa.cz') || account.secondaryEmail?.endsWith('uniqa.sk')){
                        return false;
                    }
                    else{
                        return true;
                    }
                }
                else
                {
                    return true; // vracime Ok jestli uzivatel nema HCL pravo, tzn. nekontrolujeme editace sekundarniho emailu
                }
            }

            useHead({
                meta: [
                    {
                        name: `robots`,
                        content: "noindex",
                    },
                ],
            });

            onMounted(async () => {
                loading.value = true;
                try {
                    await loadMe();
                    Object.assign(tiles, await new TilesClient().getAll());
                    Object.assign(broker, await new BrokerClient().getById(account.brokerId));
                    //SAVEBUTTON - disable save button v pripade ze nebola vykonana ziadna zmena v profile
                    //Object.assign(origAccount , new AccountDto(account));
                } catch {
                    // intercepted by axios
                }

                loading.value = false;
            });

            const fullName = computed(() => `${account.firstName} ${account.lastName}`);
            const birthDateFormated = computed(() => !account.birthDate
                ? undefined
                : new Intl.DateTimeFormat('cs-CZ').format(account.birthDate));

            const showVerifyPrompt = ref(false);
            const showPasswordVerifyPrompt = ref(false);
            let patch = [] as PatchObject[];

            const authenticate = async () => {
                try {
                    if (!verificationCodeSent) {
                        await new SecondFactorClient().authenticate(secFactor.id, recaptchaToken, true);
                        Object(verifyPrompt.value).startTimer();
                        verificationCodeSent = true;
                    }
                    showVerifyPrompt.value = true;
                } catch {
                    // intercepted by axios
                }
            };

            const resendVerificationCode = async () => {
                if (!await getRecaptchaToken()) {
                    return;
                }
                verificationCodeSent = false;
                await authenticate();
            };

            const saveChanges = async (valid: boolean) => {
                if (!isSecondaryEmailEditationOk()){
                    displaySecondaryModal.value = true;
                    return;
                }
                const updatedAccount = new AccountDto(account);
                if (general.cutEmail(origAccount.email) == updatedAccount.email) {
                    updatedAccount.email = origAccount.email;
                }
                if (updatedAccount.secondaryEmail === ""){
                    updatedAccount.secondaryEmail = undefined;
                }
                patch = diff(origAccount, updatedAccount);

                if (valid && patch.length > 0) {

                    saving.value = true;
                    try {
                        if (!account.technicalAccount && origAccount.email != updatedAccount.email) {
                            const verifyEmailUniquenessDto = new VerifyEmailUniquenessDto();
                            verifyEmailUniquenessDto.email = updatedAccount.email;
                            await accountClient.verifyMyNewEmailUniqueness(verifyEmailUniquenessDto);
                        }
                        if (account.usePasswordConfirmation) {
                            showPasswordVerifyPrompt.value = true;
                        } else {
                            secFactor = (await accountClient.getSecondFactors(userId.value))[0];
                            if (secFactor) {
                                if (!await getRecaptchaToken()) {
                                    return;
                                }
                                await authenticate();
                            } else {
                                modalMessage.value = t("userProfile.messages.missing2faError", { loginname: login.value})
                                displayModal.value = true;
                            }
                        }
                        // TODO getting second factor is duplicated with changepassword component -> move to service

                    } catch {
                        // intercepted by axios
                    }
                    saving.value = false;
                }
            }

            const continueSaving = async (verificationData: string) => {
                saving.value = true;
                try {
                    const oldEmail = origAccount.email;
                    await accountClient.patch(userId.value, patch, verificationData);

                    if (account.usePasswordConfirmation) {
                        showPasswordVerifyPrompt.value = false;
                    } else {
                        showVerifyPrompt.value = false;
                        verificationCodeSent = false;
                    }

                    try {
                        await loadMe();
                        if (!account.username && oldEmail != origAccount.email) {
                            modalMessage.value = t("userProfile.messages.newLoginName", { loginname: login.value});
                            displayModal.value = true;
                        }
                    } catch {
                        // intercepted by axios
                    }
                    //SAVEBUTTON - disable save button v pripade ze nebola vykonana ziadna zmena v profile
                    //Object.assign(origAccount , new AccountDto(account));

                    v$.value.$reset();

                    toast.add({
                        severity: 'success',
                        summary: 'Uloženo',
                        detail: 'Změny byly uloženy',
                        life: 3000
                    });
                } catch {
                    // intercepted by axios
                }

                saving.value = false;
            }

            return {
                showVerifyPrompt,
                loading,
                saving,
                savingIcon,
                v$,
                account,
                broker,
                fullName,
                birthDateFormated,
                saveChanges,
                continueSaving,
                updateEmail,
                updateSecondaryEmail,
                updatePhoneNumber,
                t,
                showPasswordVerifyPrompt,
                login,
                emailInvalid,
                emailValidStyle,
                emailChanged,
                displayModal,
                resendVerificationCode,
                verifyPrompt,
                modalMessage,
                isSSO,
                tiles,
                displaySecondaryModal,
                secondaryModalMessage,
                isSecondaryEmailEditationOk
            }
        },

        computed:{
            hasHCLRight(): boolean{
                return this.tiles.some(x => x.applicationName == 'HCL');
            },

            IsUserFromInternalDistribution(): boolean{
                const brokerNumber = Number(this.broker.brokerNumber);

                return [6000, 3000, 292, 99999].includes(brokerNumber);
            }
        }

        /*computed: {
            /*disableSaveButton(): boolean
            {
                //SAVEBUTTON - disable save button v pripade ze nebola vykonana ziadna zmena v profile
                let patch = [] as PatchObject[];
                patch = diff(this.origAccount, this.account);
                return patch.length == 0 || this.v$.$invalid || this.emailValidStyle != '';
            }*/
        //},
    });
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped lang="scss">
    @import '@/css-framework/mixins';

    .user-profile {
        & > div {
            padding: 0 1.5rem;

            @include media-breakpoint-down(md) {
                padding: 0 0.75rem;
            }
        }

        button {
            margin-bottom: 0.75rem;
        }

        .skeleton-wrapper {
            margin-bottom: 1rem;
        }

        .skeleton {
            height: 3.5rem;
        }

        .skeleton-header-wrapper {
            height: 100%;
            display: flex;
            justify-content: center;

            .skeleton-header {
                width: 40%;

                @include media-breakpoint-down(md) {
                    width: 100%;
                }
            }
        }
    }
</style>